"use client";

import { useState } from "react";
import { UserPlus, ArrowUpRight, Loader2, Truck, Phone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/fleet/status-badge";
import { Separator } from "@/components/ui/separator";
import { useDrivers } from "@/lib/hooks/use-drivers";
import { useDispatch } from "@/lib/hooks/use-dispatch";
import { toast } from "sonner";

interface AssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deliveryId: string;
  deliveryLabel: string;
}

export function AssignDialog({ open, onOpenChange, deliveryId, deliveryLabel }: AssignDialogProps) {
  const { drivers } = useDrivers(1, 100);
  const { assign, overflow } = useDispatch();
  const [loading, setLoading] = useState<string | null>(null);

  const availableDrivers = drivers.filter((d) => d.status === "available");

  async function handleAssign(driverId: string, driverName: string) {
    setLoading(driverId);
    try {
      await assign(deliveryId, driverId);
      toast.success(`Assigned to ${driverName}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to assign driver");
    } finally {
      setLoading(null);
    }
  }

  async function handleOverflow() {
    setLoading("overflow");
    try {
      await overflow(deliveryId);
      toast.success("Sent to Curri marketplace");
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to overflow");
    } finally {
      setLoading(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Delivery {deliveryLabel}</DialogTitle>
        </DialogHeader>

        <div className="space-y-2 max-h-72 overflow-y-auto">
          {availableDrivers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No available drivers
            </p>
          ) : (
            availableDrivers.map((driver) => (
              <button
                key={driver.id}
                onClick={() => handleAssign(driver.id, driver.name)}
                disabled={loading !== null}
                className="w-full flex items-center justify-between p-3 rounded-lg border hover:bg-accent transition-colors disabled:opacity-50 text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                    <Truck className="size-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{driver.name}</p>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Phone className="size-3" />
                      {driver.phone}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={driver.carrier_type} />
                  {loading === driver.id ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <UserPlus className="size-4 text-muted-foreground" />
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        <Separator />

        <Button
          variant="outline"
          onClick={handleOverflow}
          disabled={loading !== null}
          className="w-full"
        >
          {loading === "overflow" ? (
            <Loader2 className="size-4 animate-spin mr-2" />
          ) : (
            <ArrowUpRight className="size-4 mr-2" />
          )}
          Overflow to Curri Marketplace
        </Button>
      </DialogContent>
    </Dialog>
  );
}
